import React, { type CSSProperties } from 'react';
import { cn } from '../../utils/cn';
import type { MarioBottomSheetFlatListProps } from './bottom-sheet-flat-list.types';
import styles from './bottom-sheets.module.css';

/**
 * Flatten style object or array of style objects
 */
const flattenStyle = (style?: CSSProperties | CSSProperties[]): CSSProperties | undefined => {
    if (!style) return undefined;
    if (Array.isArray(style)) return Object.assign({}, ...style.filter(Boolean));
    return style;
};

/**
 * MarioBottomSheetFlatList
 *
 * A FlatList-like component for web that can be used inside bottom sheets.
 * Matches mobile's MarioBottomSheetFlatList API for code compatibility.
 *
 * @example
 * ```tsx
 * <MarioBottomSheetFlatList
 *   data={items}
 *   renderItem={({ item }) => <ItemComponent item={item} />}
 *   keyExtractor={(item) => item.id}
 *   ListEmptyComponent={<EmptyState />}
 *   horizontal
 * />
 * ```
 */
export function MarioBottomSheetFlatList<T>({
    data,
    renderItem,
    keyExtractor,
    ListEmptyComponent,
    ListHeaderComponent,
    ListFooterComponent,
    ItemSeparatorComponent,
    contentContainerClassName,
    contentContainerStyle,
    horizontal = false,
    className,
    style,
    testID,
}: MarioBottomSheetFlatListProps<T>) {
    // Handle empty state
    if (!data || data.length === 0) {
        return <>{ListEmptyComponent}</>;
    }

    const flatContainerStyle = flattenStyle(contentContainerStyle);
    const flatStyle = flattenStyle(style);

    return (
        <div
            className={cn(styles.flatList, horizontal && styles.flatListHorizontal, className)}
            style={flatStyle}
            data-testid={testID}
        >
            <div className={contentContainerClassName} style={flatContainerStyle}>
                {ListHeaderComponent}
                {data.map((item, index) => (
                    <React.Fragment key={keyExtractor ? keyExtractor(item, index) : index}>
                        {renderItem({ item, index })}
                        {ItemSeparatorComponent && index < data.length - 1 && <ItemSeparatorComponent />}
                    </React.Fragment>
                ))}
                {ListFooterComponent}
            </div>
        </div>
    );
}

MarioBottomSheetFlatList.displayName = 'MarioBottomSheetFlatList';

export default MarioBottomSheetFlatList;
